import { useMemo, useState } from 'react';
import './HeroRecommender.css';
import type { Hero } from '../types';
import { calculateWinRateDelta, calculateWinRateDeltaDetail } from '../utils/calculateWinRate';
import { compareRecommendationByDeltaDesc } from '../utils/recommendationSort';
import { filterHeroesByLaneMode, type LaneFilterMode } from './heroFilter';
import { RecommendationCard } from './RecommendationCard';
import { RecommenderAdvancedFilters } from './RecommenderAdvancedFilters';
import { STANDARD_DRAFT_LANES } from '../utils/laneAssignment';
import type { CounterReasonEntry, SynergyReasonEntry } from '../utils/winRateDelta';

interface HeroRecommenderProps {
  heroes: Hero[];
  allyHeroes: Hero[];
  enemyHeroes: Hero[];
  onAddToAlly: (heroId: number) => void;
  onAddToEnemy: (heroId: number) => void;
}

interface HeroRecommendation {
  hero: Hero;
  winRateDelta: number;
  baseDelta: number;
  counterReasons: CounterReasonEntry[];
  synergyReasons: SynergyReasonEntry[];
  residualDelta: number;
}

const LANE_LABEL: Record<string, string> = {
  Roam: 'ローム',
  'Mid Lane': 'ミッド',
  'Gold Lane': 'ゴールド',
  'Exp Lane': 'EXP',
  Jungle: 'ジャングル',
};

const LANE_OPTIONS = STANDARD_DRAFT_LANES.map(value => ({
  value,
  label: LANE_LABEL[value] ?? value,
}));

export function HeroRecommender({
  heroes,
  allyHeroes,
  enemyHeroes,
  onAddToAlly,
  onAddToEnemy,
}: HeroRecommenderProps) {
  const [activeTab, setActiveTab] = useState<'ally' | 'enemy'>('ally');
  const [filterMode, setFilterMode] = useState<LaneFilterMode>('smart');
  const [selectedLanes, setSelectedLanes] = useState<string[]>([]);

  const filteredHeroes = useMemo(() => {
    const mode = filterMode === 'smart' ? 'smart' : selectedLanes;
    return filterHeroesByLaneMode(heroes, allyHeroes, enemyHeroes, mode, activeTab);
  }, [heroes, allyHeroes, enemyHeroes, filterMode, selectedLanes, activeTab]);

  const recommendations = useMemo(() => {
    const isAlly = activeTab === 'ally';
    const recs: HeroRecommendation[] = filteredHeroes.map(hero => {
      const winRateDelta = calculateWinRateDelta(hero, allyHeroes, enemyHeroes, isAlly);
      const detail = calculateWinRateDeltaDetail(hero, allyHeroes, enemyHeroes, isAlly);
      return {
        hero,
        winRateDelta,
        baseDelta: detail.baseDelta,
        counterReasons: detail.counterReasons,
        synergyReasons: detail.synergyReasons,
        residualDelta: detail.residualDelta,
      };
    });
    return [...recs].sort(compareRecommendationByDeltaDesc);
  }, [filteredHeroes, allyHeroes, enemyHeroes, activeTab]);

  const handleSelect = (heroId: number) => {
    if (activeTab === 'ally') {
      onAddToAlly(heroId);
    } else {
      onAddToEnemy(heroId);
    }
  };

  const handleLaneToggle = (lane: string) => {
    setSelectedLanes(prev => {
      if (prev.includes(lane)) {
        return prev.filter(l => l !== lane);
      }
      return [...prev, lane];
    });
    setFilterMode('manual');
  };

  const handleModeChange = (mode: 'smart' | 'manual') => {
    setFilterMode(mode);
    if (mode === 'smart') {
      setSelectedLanes([]);
    }
  };

  return (
    <div className={`hero-recommender hero-recommender--${activeTab}`}>
      <div className="recommender-header">
        <h3>おすすめヒーロー</h3>
        <div className="tab-switcher" role="tablist" aria-label="おすすめの対象チーム">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'ally'}
            className={`tab tab--ally ${activeTab === 'ally' ? 'active' : ''}`}
            onClick={() => setActiveTab('ally')}
            aria-label="味方チーム向けのおすすめを表示"
          >
            味方
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'enemy'}
            className={`tab tab--enemy ${activeTab === 'enemy' ? 'active' : ''}`}
            onClick={() => setActiveTab('enemy')}
            aria-label="敵チーム向けのおすすめを表示"
          >
            敵
          </button>
        </div>
      </div>

      <RecommenderAdvancedFilters
        filterMode={filterMode}
        selectedLanes={selectedLanes}
        laneOptions={LANE_OPTIONS}
        onModeChange={handleModeChange}
        onLaneToggle={handleLaneToggle}
      />

      <div className="recommendations-list">
        {allyHeroes.length === 0 && enemyHeroes.length === 0 ? (
          <div className="empty-message">
            <p>ピックを確定してください</p>
            <p className="empty-sub">
              自チームまたは敵チームにヒーローを選択すると、おすすめヒーローが表示されます
            </p>
          </div>
        ) : (
          recommendations.slice(0, 5).map(
            ({ hero, winRateDelta, baseDelta, counterReasons, synergyReasons, residualDelta }) => (
              <RecommendationCard
                key={hero.id}
                hero={hero}
                winRateDelta={winRateDelta}
                reasons={{
                  baseDelta,
                  counterReasons,
                  synergyReasons,
                  residualDelta,
                }}
                pickLabel={activeTab === 'ally' ? '味方' : '敵'}
                onSelect={() => handleSelect(hero.id)}
              />
            )
          )
        )}
      </div>
    </div>
  );
}
